// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component, forwardRef } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { inject, observer } from "../../../../stores/mobx-compat";
import 'react-datepicker/dist/react-datepicker.css';
import '../Emails/datepicker-overrides.css';

import Card from '../../../ui/Card';
import Button from '../../../ui/Button';
import Alert from '../../../ui/Alert';
import Pill from '../../../ui/Pill';
import Empty from '../../../ui/Empty';
import Table, {
  Th, Td, Tr, TableToolbar, TablePager,
} from '../../../ui/Table';
import {
  RefreshIcon, CheckCircleIcon, ExternalLinkIcon, CalendarIcon, ChevronDownIcon,
} from '../../../ui/icons';

const PAGE_SIZE = 50;

/** Recovery emails are 0-indexed by the remarket cron, so display as 1-based */
const emailLabel = (remarketId) => `Email #${(Number(remarketId) || 0) + 1}`;

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const d = moment.duration(Number(seconds), 'seconds');
  if (d.asMinutes() < 60) return `${Math.max(Math.round(d.asMinutes()), 1)}m`;
  if (d.asHours() < 24) return `${Math.round(d.asHours())}h`;
  return `${Math.round(d.asDays())}d`;
};

class RecoveredForms extends Component {
  constructor(props) {
    super(props);
    this.state = {
      startDate: moment().subtract(30, 'day').toDate(),
      endDate: new Date(),
      preset: 30,
    };
  }

  componentDidMount() {
    this.fetch(1);
  }

  fetch = (page = 1) => {
    const { startDate, endDate } = this.state;
    this.props.store.analytics.getRecoveredForms({
      startDate,
      endDate,
      page,
      pageSize: PAGE_SIZE,
    });
  }

  onRangeChange = ([start, end]) => this.setState({ startDate: start, endDate: end, preset: null });

  setPreset = (days) => this.setState({
    startDate: moment().subtract(days, 'day').toDate(),
    endDate: new Date(),
    preset: days,
  });

  render() {
    const { recoveredForms, recoveredFormsPagination, loading } = this.props.store.analytics;
    const { startDate, endDate, preset } = this.state;
    const busy = loading.getRecoveredForms;

    return (
      <>
        <Alert tone="info" title="Forms recovered by your recovery emails">
          Every lead that abandoned a form, came back through a recovery email, and completed it.
          Sorted by most recently recovered.
        </Alert>

        <Card padded className="mt-5">
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="mov-rangepicker mov-rangepicker--pop">
              <DatePicker
                selectsRange
                showPopperArrow={false}
                startDate={startDate}
                endDate={endDate}
                onChange={this.onRangeChange}
                monthsShown={2}
                maxDate={new Date()}
                dateFormat="MM/dd/yyyy"
                popperPlacement="bottom-start"
                customInput={<RangeInput />}
              />
            </div>

            <div className="mov-presets">
              {[7, 30, 90].map((d) => (
                <PresetChip
                  key={d}
                  label={`${d}d`}
                  active={preset === d}
                  onClick={() => this.setPreset(d)}
                />
              ))}
            </div>

            <Button
              variant="primary"
              icon={RefreshIcon}
              loading={busy}
              disabled={busy}
              onClick={() => this.fetch(1)}
              className="ml-auto"
            >
              Apply
            </Button>
          </div>
        </Card>

        <Card padded={false} className="mt-5">
          <TableToolbar title="Recovered forms" count={recoveredFormsPagination.total} />
          {recoveredForms.length === 0 && !busy ? (
            <Empty
              icon={CheckCircleIcon}
              title="No recovered forms"
              sub="No recoveries match the selected date range."
            />
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th>Email</Th>
                    <Th>Recovered</Th>
                    <Th>Abandoned</Th>
                    <Th>Time to recover</Th>
                    <Th>Recovered by</Th>
                    <Th>Page</Th>
                  </tr>
                </thead>
                <tbody>
                  {recoveredForms.map((rf) => (
                    <Tr key={rf.mov_id}>
                      <Td>{rf.email}</Td>
                      <Td mute>{moment(rf.recovered_ts).format('MMM DD, YYYY · HH:mm')}</Td>
                      <Td mute>{moment(rf.abandoned_ts).format('MMM DD, YYYY · HH:mm')}</Td>
                      <Td>
                        <Pill tone="green" dot>{formatDuration(rf.seconds_to_recover)}</Pill>
                      </Td>
                      <Td>
                        <span className="mov-mono text-[12px]" style={{ wordBreak: 'break-all' }}>
                          {rf.creative || emailLabel(rf.remarket_id)}
                        </span>
                      </Td>
                      <Td>
                        {rf.page_url ? (
                          <a
                            href={rf.page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-mov-accent-ink underline"
                            style={{ wordBreak: 'break-all' }}
                          >
                            <ExternalLinkIcon size={12} className="shrink-0" />
                            {rf.host}{rf.path}
                          </a>
                        ) : <span className="text-mov-ink-mute">—</span>}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              <TablePager
                page={recoveredFormsPagination.page}
                totalPages={recoveredFormsPagination.totalPages}
                total={recoveredFormsPagination.total}
                onChange={(p) => this.fetch(p)}
              />
            </>
          )}
        </Card>
      </>
    );
  }
}

const RangeInput = forwardRef(({ value, onClick }, ref) => (
  <button ref={ref} type="button" onClick={onClick} className="mov-daterange">
    <CalendarIcon size={14} className="text-mov-ink-mute" />
    {value || 'Select date range'}
    <ChevronDownIcon size={12} className="text-mov-ink-mute" />
  </button>
));

const PresetChip = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={!!active}
    className={`mov-preset ${active ? 'is-on' : ''}`}
  >
    {label}
  </button>
);

export default inject('store')(observer(RecoveredForms));
